import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import { completeNewPassword, signIn, submitMfa } from "@/auth/cognito";
import type { CognitoUser } from "amazon-cognito-identity-js";

type Stage =
  | { kind: "credentials" }
  | { kind: "newPassword"; user: CognitoUser; userAttributes: Record<string, string> }
  | { kind: "mfa"; user: CognitoUser };

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stage, setStage] = useState<Stage>({ kind: "credentials" });
  const [newPassword, setNewPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const redirectTo = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = await signIn(email, password);
    setBusy(false);

    switch (result.kind) {
      case "ok":
        await refresh();
        navigate(redirectTo, { replace: true });
        return;
      case "newPasswordRequired":
        setStage({ kind: "newPassword", user: result.user, userAttributes: result.userAttributes });
        return;
      case "mfaRequired":
        setStage({ kind: "mfa", user: result.user });
        return;
      case "error":
        setError(result.message);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== "newPassword") return;
    setBusy(true);
    setError(null);
    const result = await completeNewPassword(stage.user, newPassword, stage.userAttributes);
    setBusy(false);
    if (result.kind === "ok") {
      await refresh();
      navigate(redirectTo, { replace: true });
    } else if (result.kind === "mfaRequired") {
      setStage({ kind: "mfa", user: result.user });
    } else if (result.kind === "error") {
      setError(result.message);
    }
  }

  async function handleMfa(e: React.FormEvent) {
    e.preventDefault();
    if (stage.kind !== "mfa") return;
    setBusy(true);
    setError(null);
    const result = await submitMfa(stage.user, mfaCode);
    setBusy(false);
    if (result.kind === "ok") {
      await refresh();
      navigate(redirectTo, { replace: true });
    } else if (result.kind === "error") {
      setError(result.message);
    }
  }

  return (
    <div style={{ maxWidth: 400, margin: "60px auto", background: "white", padding: 32, borderRadius: 8, border: "1px solid var(--color-border)" }}>
      <h1 style={{ marginTop: 0 }}>Sign in</h1>

      {stage.kind === "credentials" && (
        <form onSubmit={handleSignIn}>
          <label>Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          <div className="spacer" />
          <label>Password</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          <div className="spacer" />
          <button type="submit" className="primary" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </form>
      )}

      {stage.kind === "newPassword" && (
        <form onSubmit={handleNewPassword}>
          <p className="muted">You must set a new password before continuing.</p>
          <label>New password</label>
          <input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoFocus />
          <div className="spacer" />
          <button type="submit" className="primary" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Updating..." : "Set password"}
          </button>
        </form>
      )}

      {stage.kind === "mfa" && (
        <form onSubmit={handleMfa}>
          <p className="muted">Enter the 6-digit code from your authenticator app.</p>
          <label>MFA code</label>
          <input type="text" inputMode="numeric" required maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} autoFocus />
          <div className="spacer" />
          <button type="submit" className="primary" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Verifying..." : "Verify"}
          </button>
        </form>
      )}

      {error && <p className="error">{error}</p>}
    </div>
  );
}
